import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../auth/useAuth0';
import { useBusinessContext } from '../../../../context/BusinessContext';
import { useAlert } from '../../../../context/alert/AlertContext';
import { useConfirm } from '../../../../context/confirm/ConfirmContext';
import LoadingComponent from '../../../commons/LoadingComponent';
import { Service, RecurrenceFreq, AcceptedPlan, RepasseType, ServiceKind, effectiveRepasse } from '../../../../model/service/Service';
import { Category } from '../../../../model/category/Category';
import { ServiceInput } from '../../../../service/service/ServiceInput';
import {
    deleteService,
    saveService,
} from '../../../../service/service/service-service';
import { HealthPlan } from '../../../../model/business/HealthPlan';
import { useQueryClient } from '@tanstack/react-query';
import { useServices, useCategories, useBusinessDetails, useHealthPlans } from '../../../../hooks/queries';
import MoneyInput from '../../../commons/money-input/MoneyInput';
import { currencySymbol, formatMoney } from '../../../../util/money';
import { PRICE_TYPE_OPTIONS, PriceType } from '../../../../model/common/PriceType';
import Drawer from '../../../commons/drawer/Drawer';
import Pagination from '../../../commons/pagination/Pagination';
import { NumberWithSuffix } from '../../../commons/settings-row/SettingsRow';
import { uploadImage, mediaUrl } from '../../../../service/media/media-service';
import ShareBookingModal from '../components/ShareBookingModal';
import { findBusinessSettingByBusinessId } from '../../../../service/business/business-service';

const emptyDraft = (businessId: number): ServiceInput => ({
    businessId,
    name: '',
    description: '',
    categories: [],
    color: '#0d6efd',
    showOnWebSite: true,
    image: '',
    kind: 'SERVICE',
    capacity: null,
    totalDuration: 30,
    price: 0,
    priceType: 'FIXED',
    taxRate: null,
    depositRequired: false,
    amountDepositRequired: 0,
    allowRecurring: false,
    recurrenceFreqs: [],
    maxRecurrenceMonths: 3,
    allowOnlineBooking: true,
    bookingWindow: null,
    allowExternalLocation: false,
    startPreparationDuration: null,
    endAfterDuration: null,
    allowBookingDuringPreparationDuration: false,
    allowBookingDuringAfterDuration: false,
    acceptedPlans: [],
    isHealthDataOverride: null,
});

/** Editor drawer tabs, in order. New services walk through them with "Next". */
type ServiceTab = 'details' | 'payment' | 'booking';
const TAB_ORDER: ServiceTab[] = ['details', 'payment', 'booking'];

const RECURRENCE_FREQ_KEYS: RecurrenceFreq[] = [
    'DAILY',
    'WEEKLY',
    'BIWEEKLY',
    'MONTHLY',
    'WEEKLY_DAYS',
];

interface ServicesTabProps {
    /** Which catalog kind this tab manages. Services tab = SERVICE (default),
     *  Events tab = EVENT. The list is filtered and new items default to it. */
    kind?: ServiceKind;
}

const ServicesTab: React.FC<ServicesTabProps> = ({ kind = 'SERVICE' }) => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    /** Local toggle for the "accepts health plans" section (derived from the
     *  draft's acceptedPlans on open; unchecking clears the list on save). */
    const [acceptsConvenio, setAcceptsConvenio] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<ServiceInput | null>(null);
    const [saving, setSaving] = useState(false);
    /** Which section of the editor drawer is showing. Split so the form isn't
     *  one long, uneven scroll: identity / pricing+plans / booking rules. */
    const [serviceTab, setServiceTab] = useState<ServiceTab>('details');
    // Duration input unit (UI only — totalDuration is always stored in minutes).
    const [durationUnit, setDurationUnit] = useState<'MINUTES' | 'HOURS'>('MINUTES');

    /** Text typed into the "Add category…" search box for filtering matches. */
    const [catSearch, setCatSearch] = useState('');
    /** Controls visibility of the matches dropdown below the categories search. */
    const [catDropdownOpen, setCatDropdownOpen] = useState(false);

    /** Pagination — keeps the list manageable for long catalogs. */
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    /** Free-text filter on service name (case-insensitive substring). */
    const [search, setSearch] = useState('');

    /** Share booking link modal state. */
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareInitialServiceId, setShareInitialServiceId] = useState<string | undefined>(undefined);

    const businessId = Number(selectedBusiness?.id) || 0;

    // Cached via React Query so revisiting the Services tab is instant and
    // refreshes in the background instead of refetching on every mount.
    const queryClient = useQueryClient();
    const servicesQuery = useServices(businessId);
    const categoriesQuery = useCategories(businessId);
    const detailsQuery = useBusinessDetails(businessId || undefined);
    const healthPlansQuery = useHealthPlans(businessId);
    const services: Service[] = (servicesQuery.data ?? []).filter((sv: Service) => (sv.kind ?? 'SERVICE') === kind);
    const categories: Category[] = categoriesQuery.data ?? [];

    /** Prontuário master switch — shows the per-service override control. */
    const [healthMode, setHealthMode] = React.useState(false);
    React.useEffect(() => {
        if (!businessId) return;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const settings = await findBusinessSettingByBusinessId(businessId, token);
                setHealthMode(!!settings?.healthMode);
            } catch { setHealthMode(false); }
        })();
    }, [businessId, getAccessTokenSilently]);
    const companyTaxRate: number | null = detailsQuery.data?.retailsSalesTaxRate ?? null;
    // Only active plans are offered for new selections.
    const healthPlans: HealthPlan[] = ((healthPlansQuery.data ?? []) as HealthPlan[]).filter((p) => p.active !== false);
    const loading = servicesQuery.isLoading || categoriesQuery.isLoading;
    const reload = () => {
        queryClient.invalidateQueries({ queryKey: ['services', String(businessId)] });
        queryClient.invalidateQueries({ queryKey: ['categories', String(businessId)] });
        queryClient.invalidateQueries({ queryKey: ['healthPlans', String(businessId)] });
    };

    useEffect(() => {
        const err = servicesQuery.error || categoriesQuery.error;
        if (err) setAlert('danger', err instanceof Error ? err.message : t('services.alerts.failLoadServices'));
    }, [servicesQuery.error, categoriesQuery.error, setAlert, t]);

    const startCreate = () => {
        setSelectedId(null);
        setServiceTab('details');
        setAcceptsConvenio(false);
        setDraft({ ...emptyDraft(businessId), kind });
    };

    const startEdit = (s: Service) => {
        setSelectedId(s.id);
        setServiceTab('details');
        setAcceptsConvenio((s.acceptedPlans?.length ?? 0) > 0);
        setDraft({
            id: s.id,
            businessId: Number(s.businessId) || businessId,
            name: s.name ?? '',
            description: s.description ?? '',
            categories: s.categories ?? [],
            color: s.color ?? '#0d6efd',
            showOnWebSite: s.showOnWebSite ?? true,
            image: s.image ?? '',
            kind: s.kind ?? 'SERVICE',
            capacity: s.capacity ?? null,
            totalDuration: s.totalDuration ?? 30,
            startPreparationDuration: s.startPreparationDuration ?? null,
            endPreparationDuration: s.endPreparationDuration ?? null,
            allowBookingDuringPreparationDuration: s.allowBookingDuringPreparationDuration ?? null,
            startAfterDuration: s.startAfterDuration ?? null,
            endAfterDuration: s.endAfterDuration ?? null,
            allowBookingDuringAfterDuration: s.allowBookingDuringAfterDuration ?? null,
            price: s.price ?? 0,
            priceType: s.priceType ?? 'FIXED',
            taxRate: s.taxRate ?? null,
            depositRequired: s.depositRequired ?? false,
            amountDepositRequired: s.amountDepositRequired ?? 0,
            allowRecurring: s.allowRecurring ?? false,
            isHealthDataOverride: s.isHealthDataOverride ?? null,
            recurrenceFreqs: s.recurrenceFreqs ?? [],
            maxRecurrenceMonths: s.maxRecurrenceMonths ?? 3,
            allowOnlineBooking: s.allowOnlineBooking ?? true,
            bookingWindow: s.bookingWindow ?? null,
            allowExternalLocation: s.allowExternalLocation ?? false,
            acceptedPlans: (s.acceptedPlans ?? []).map((p) => ({
                healthPlanId: p.healthPlanId,
                negotiatedValue: p.negotiatedValue ?? null,
            })),
        });
    };

    /** Whether a given health plan is currently selected on the draft. */
    const isPlanAccepted = (planId: number) =>
        !!draft?.acceptedPlans?.some((p) => p.healthPlanId === planId);

    /** Add/remove a plan from the draft's accepted list. New plans default the
     *  repasse to a fixed VALUE (the attendant can switch to %). */
    const togglePlan = (planId: number) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const current = prev.acceptedPlans ?? [];
            const next = current.some((p) => p.healthPlanId === planId)
                ? current.filter((p) => p.healthPlanId !== planId)
                : [...current, { healthPlanId: planId, negotiatedValue: null, repasseType: 'VALUE' as const, repasseValue: null }];
            return { ...prev, acceptedPlans: next };
        });
    };

    /** Update one field of a plan row (table value or repasse). */
    const updatePlan = (planId: number, patch: Partial<AcceptedPlan>) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const current = prev.acceptedPlans ?? [];
            return {
                ...prev,
                acceptedPlans: current.map((p) =>
                    p.healthPlanId === planId ? { ...p, ...patch } : p
                ),
            };
        });
    };

    /** Values are stored exactly as typed — over-the-limit values are flagged
     *  in red and blocked on Save (and rejected by the backend); we never
     *  silently rewrite the user's number. */

    /** Master toggle: turning it off clears the accepted list; turning it on
     *  also clears the deposit (a covered service isn't booked with a deposit). */
    const toggleAcceptsConvenio = (on: boolean) => {
        setAcceptsConvenio(on);
        setDraft((prev) => {
            if (!prev) return prev;
            if (on) {
                return { ...prev, depositRequired: false, amountDepositRequired: 0 };
            }
            return { ...prev, acceptedPlans: [] };
        });
    };

    /** Variable-price services can't carry a negotiated plan table (the value
     *  isn't known up front), so switching to VARIABLE turns convênio off. */
    const handlePriceTypeChange = (pt: PriceType) => {
        setDraft((prev) => (prev ? { ...prev, priceType: pt } : prev));
        if (pt === 'VARIABLE') {
            setAcceptsConvenio(false);
            handleChange('acceptedPlans', []);
        }
    };

    /** Advance to the next tab (new-service wizard). Validates the name before
     *  leaving Details so the required field isn't skipped. */
    const goNextTab = () => {
        if (serviceTab === 'details' && (!draft?.name || !draft.name.trim())) {
            setAlert('danger', t('services.alerts.serviceNameRequired'));
            return;
        }
        const i = TAB_ORDER.indexOf(serviceTab);
        if (i < TAB_ORDER.length - 1) setServiceTab(TAB_ORDER[i + 1]);
    };

    const toggleRecurrenceFreq = (freq: RecurrenceFreq) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const current = prev.recurrenceFreqs ?? [];
            const next = current.includes(freq)
                ? current.filter((f) => f !== freq)
                : [...current, freq];
            return { ...prev, recurrenceFreqs: next };
        });
    };

    const cancelEdit = () => {
        setSelectedId(null);
        setDraft(null);
    };

    const handleChange = <K extends keyof ServiceInput>(field: K, value: ServiceInput[K]) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        if (!businessId) { setAlert('danger', t('services.alerts.selectBusiness')); return; }
        try {
            const token = await getAccessTokenSilently();
            const key = await uploadImage(file, `service/${businessId}`, token);
            handleChange('image', key);
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('services.alerts.failUploadImage'));
        }
    };

    const toggleCategory = (categoryId: string) => {
        if (!draft) return;
        const current = draft.categories ?? [];
        const next = current.includes(categoryId)
            ? current.filter((id) => id !== categoryId)
            : [...current, categoryId];
        handleChange('categories', next);
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!draft.name || !draft.name.trim()) {
            setServiceTab('details'); // the required field lives on the Details tab
            setAlert('danger', t('services.alerts.serviceNameRequired'));
            return;
        }
        if (!businessId) {
            setAlert('danger', t('services.alerts.noBusinessSelected'));
            return;
        }
        // Convênio: validate table value ≤ price and repasse limits. The backend
        // rejects these too — this just gives a clear message before the round-trip.
        if (acceptsConvenio) {
            const plans = draft.acceptedPlans ?? [];
            const tableOver = typeof draft.price === 'number'
                && plans.some((p) => p.negotiatedValue != null && p.negotiatedValue > draft.price!);
            const repasseBad = plans.some((p) => {
                if (p.repasseValue == null) return false;
                if (p.repasseType === 'PERCENT') return p.repasseValue > 100;
                return p.negotiatedValue != null && p.repasseValue > p.negotiatedValue;
            });
            if (tableOver || repasseBad) {
                setServiceTab('payment');
                setAlert('danger', t(tableOver
                    ? 'services.alerts.convenioValueExceedsPrice'
                    : 'services.alerts.convenioRepasseInvalid'));
                return;
            }
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const saved = await saveService({ ...draft, businessId }, token);
            setAlert('success', t('services.alerts.serviceSaved', { name: saved.name }));
            // Close the drawer on success (don't re-open the editor).
            cancelEdit();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.alerts.failSaveService'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft?.id) return;
        if (!(await confirmDialog({ title: t('common.confirmTitle'), message: t('services.alerts.confirmDeleteService', { name: draft.name }), danger: true, confirmLabel: t('common.delete') }))) return;
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await deleteService(draft.id, token);
            setAlert('success', t('services.alerts.serviceDeleted'));
            cancelEdit();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.alerts.failDeleteService'));
        } finally {
            setSaving(false);
        }
    };


    if (loading) return <LoadingComponent />;

    // Filter + paginate — search runs against the raw list, pagination
    // applies on the filtered subset.
    const q = search.trim().toLowerCase();
    const filteredServices = q
        ? services.filter((s) =>
              (s.name ?? '').toLowerCase().includes(q) ||
              (s.description ?? '').toLowerCase().includes(q)
          )
        : services;

    // Category lookup + grouping. A service can carry several categories; for
    // grouping we file it under its alphabetically-first category (and still
    // show every category chip on the row). Services with none fall into an
    // "Uncategorized" bucket that always sorts last.
    const UNCATEGORIZED = t('services.uncategorized');
    const categoryNameById = (id: string) =>
        categories.find((c) => c.id === id)?.name || '';
    const groupNameOf = (s: Service): string => {
        const names = (s.categories ?? [])
            .map(categoryNameById)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        return names[0] || UNCATEGORIZED;
    };
    // Sort by group (alpha, Uncategorized last) then by service name (alpha).
    const sortedServices = [...filteredServices].sort((a, b) => {
        const ga = groupNameOf(a);
        const gb = groupNameOf(b);
        if (ga !== gb) {
            if (ga === UNCATEGORIZED) return 1;
            if (gb === UNCATEGORIZED) return -1;
            return ga.localeCompare(gb);
        }
        return (a.name ?? '').localeCompare(b.name ?? '');
    });

    const totalPages = Math.max(1, Math.ceil(sortedServices.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * perPage;
    const visibleServices = sortedServices.slice(
        pageStart,
        pageStart + perPage
    );

    return (
        <>
            <div className="list-card">
                <div className="list-header">
                    <h5 className="mb-0">{kind === 'EVENT' ? t('services.eventsTitle') : t('services.servicesTitle')}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>
                            ⌕
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('services.searchServicesPlaceholder')}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                        {search && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => {
                                    setSearch('');
                                    setPage(1);
                                }}
                                aria-label={t('common.clear')}
                                title={t('common.clear')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => { setShareInitialServiceId(undefined); setShareModalOpen(true); }}
                        data-cy="service-share-btn"
                    >
                        {t('share.button')}
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={startCreate} data-cy="service-add-btn">
                        {kind === 'EVENT' ? t('services.addNewEvent') : t('services.addNew')}
                    </button>
                </div>

                {services.length === 0 && (
                    <div className="empty-hint">{t('services.emptyServices')}</div>
                )}

                {services.length > 0 && filteredServices.length === 0 && (
                    <div className="empty-hint">
                        {t('services.emptyServicesSearch', { search })}
                    </div>
                )}

                {visibleServices.map((s, i) => {
                    // Category names for this service's chips.
                    const catNames = (s.categories ?? [])
                        .map(categoryNameById)
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b));
                    // Render a group header whenever the category changes from
                    // the previous row on this page.
                    const group = groupNameOf(s);
                    const prevGroup = i > 0 ? groupNameOf(visibleServices[i - 1]) : null;
                    const showHeader = group !== prevGroup;
                    return (
                        <React.Fragment key={s.id}>
                            {showHeader && (
                                <div className="svc-group-header">{group}</div>
                            )}
                            <div
                                className={`list-item ${selectedId === s.id ? 'selected' : ''}`}
                                onClick={() => startEdit(s)}
                                data-cy={`service-row-${s.id}`}
                            >
                                <div>
                                    <span
                                        className="color-swatch"
                                        style={{ background: s.color ?? '#ccc' }}
                                    />
                                    <span className="name">{s.name || '(no name)'}</span>
                                    <div className="meta">
                                        {s.totalDuration ? `${s.totalDuration} min` : '— min'}
                                        {' · '}
                                        {s.priceType === 'VARIABLE' ? t('services.priceFrom') : ''}
                                        {formatMoney(typeof s.price === 'number' ? s.price : 0, selectedBusiness?.currency)}
                                        {catNames.length > 0 && (
                                            <span className="svc-cat-chips">
                                                {catNames.map((n) => (
                                                    <span key={n} className="svc-cat-chip">{n}</span>
                                                ))}
                                            </span>
                                        )}
                                    </div>
                                    {/* At-a-glance attributes: price type, online/in-person,
                                        accepts a health plan, recurring. Each is text-labelled
                                        (not color-only) so it's readable; convênio/recurring
                                        only appear when on, to keep the row uncluttered. */}
                                    <div className="svc-attrs">
                                        {s.kind === 'EVENT' && (
                                            <span className="svc-attr svc-attr--event" data-cy={`service-event-badge-${s.id}`}>
                                                👥 {t('services.badgeEvent')}
                                            </span>
                                        )}
                                        <span className="svc-attr svc-attr--price">
                                            {t(s.priceType === 'VARIABLE' ? 'services.priceVariable' : 'services.priceFixed')}
                                        </span>
                                        <span className={`svc-attr ${s.allowOnlineBooking !== false ? 'svc-attr--online' : 'svc-attr--inperson'}`}>
                                            {t(s.allowOnlineBooking !== false ? 'services.badgeOnline' : 'services.badgeInPerson')}
                                        </span>
                                        {(s.acceptedPlans?.length ?? 0) > 0 && (
                                            <span className="svc-attr svc-attr--convenio">
                                                {t('services.badgeConvenio')}
                                            </span>
                                        )}
                                        {s.depositRequired && s.kind !== 'EVENT' && (
                                            <span className="svc-attr svc-attr--deposit">
                                                {t('services.badgeDeposit')}
                                            </span>
                                        )}
                                        {s.allowRecurring && (
                                            <span className="svc-attr svc-attr--recurring">
                                                {t('services.badgeRecurring')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 ms-auto"
                                    style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShareInitialServiceId(s.id);
                                        setShareModalOpen(true);
                                    }}
                                    title={t('share.rowAction')}
                                    data-cy={`service-share-row-${s.id}`}
                                >
                                    {t('share.rowAction')}
                                </button>
                            </div>
                        </React.Fragment>
                    );
                })}

                <Pagination
                    page={safePage}
                    perPage={perPage}
                    totalItems={filteredServices.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => {
                        setPerPage(n);
                        setPage(1);
                    }}
                    itemLabel="service"
                />
            </div>

            <ShareBookingModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                slug={selectedBusiness?.slug}
                services={services.map((s) => ({ id: s.id, name: s.name ?? '' }))}
                initialServiceId={shareInitialServiceId}
            />

            <Drawer
                open={!!draft}
                onClose={cancelEdit}
                title={draft?.id
                    ? (kind === 'EVENT' ? t('services.drawerEditEvent') : t('services.drawerEditService'))
                    : (kind === 'EVENT' ? t('services.drawerNewEvent') : t('services.drawerNewService'))}
                size="lg"
                footer={
                    draft && (
                        <>
                            {/* New service: walk through every tab — each non-last
                               tab shows "Next", the last (Booking) shows Save.
                               Editing: Save is always available on any tab. */}
                            {!draft.id && serviceTab !== 'booking' ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={goNextTab}
                                    disabled={saving}
                                    data-cy="service-next-btn"
                                >
                                    {t('common.next')}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                    data-cy="service-save-btn"
                                >
                                    {saving ? t('common.working') : t('common.save')}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={cancelEdit}
                                disabled={saving}
                                data-cy="service-cancel-btn"
                            >
                                {t('common.cancel')}
                            </button>
                            {draft.id && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    onClick={handleDelete}
                                    disabled={saving}
                                    data-cy="service-delete-btn"
                                >
                                    {t('common.delete')}
                                </button>
                            )}
                        </>
                    )
                }
            >
                {draft && (
                    <form className="event-form" onSubmit={(e) => e.preventDefault()} data-cy="service-drawer">
                        <div className="gs-tab-strip">
                            <button
                                type="button"
                                className={`gs-tab ${serviceTab === 'details' ? 'gs-tab--active' : ''}`}
                                onClick={() => setServiceTab('details')}
                                data-cy="service-tab-details"
                            >
                                {t('services.tabDetails')}
                            </button>
                            <button
                                type="button"
                                className={`gs-tab ${serviceTab === 'payment' ? 'gs-tab--active' : ''}`}
                                onClick={() => setServiceTab('payment')}
                                data-cy="service-tab-payment"
                            >
                                {t('services.tabPayment')}
                            </button>
                            <button
                                type="button"
                                className={`gs-tab ${serviceTab === 'booking' ? 'gs-tab--active' : ''}`}
                                onClick={() => setServiceTab('booking')}
                                data-cy="service-tab-scheduling"
                            >
                                {t('services.tabBooking')}
                            </button>
                        </div>

                        {serviceTab === 'details' && (
                        <>
                        {/* Kind is decided by the tab (Serviços vs Eventos) — no
                            picker here. New drafts default to the tab's kind. */}

                        <div className="form-group">
                            <label>
                                {(draft.kind ?? 'SERVICE') === 'EVENT'
                                    ? t('services.eventName')
                                    : t('services.serviceName')} <span className="gs-required">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                maxLength={255}
                                value={draft.name ?? ''}
                                onChange={(e) => handleChange('name', e.target.value)}
                                data-cy="service-name-input"
                            />
                        </div>

                        {(draft.kind ?? 'SERVICE') === 'EVENT' && (
                            <div className="form-group">
                                <label>{t('services.eventCapacity')}</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control"
                                    style={{ maxWidth: 160 }}
                                    maxLength={5}
                                    value={draft.capacity ?? ''}
                                    onChange={(e) => {
                                        // dígitos apenas; vazio = sem limite
                                        const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                                        handleChange('capacity', digits === '' ? null : Number(digits));
                                    }}
                                    placeholder={t('services.eventCapacityPlaceholder')}
                                    data-cy="service-capacity-input"
                                />
                                <small className="form-text text-muted">{t('services.eventCapacityHint')}</small>
                            </div>
                        )}

                        <div className="form-group">
                            <label>{t('services.serviceDescription')}</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={draft.description ?? ''}
                                onChange={(e) => handleChange('description', e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('services.serviceImage')}</label>
                            <input
                                type="file"
                                accept="image/*"
                                className="form-control"
                                onChange={handleImageUpload}
                                style={{ marginBottom: '0.5rem' }}
                            />
                            <input
                                type="text"
                                className="form-control"
                                value={draft.image ?? ''}
                                onChange={(e) => handleChange('image', e.target.value)}
                                placeholder={t('services.serviceImageUrlPlaceholder')}
                            />
                            {draft.image ? (
                                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                <img src={mediaUrl(draft.image)} alt="preview"
                                    style={{ marginTop: '0.5rem', maxHeight: 100, borderRadius: 8 }} />
                            ) : null}
                        </div>

                        <div className="form-group">
                            <label>{t('services.serviceCategories')}</label>
                            {categories.length === 0 ? (
                                <div className="empty-hint" style={{ padding: '0.25rem 0' }}>
                                    {t('services.noCategoriesYet')}
                                </div>
                            ) : (
                                (() => {
                                    const selectedCats = (draft.categories ?? [])
                                        .map((id) => categories.find((c) => c.id === id))
                                        .filter((c): c is Category => !!c);
                                    const q = catSearch.trim().toLowerCase();
                                    const availableMatches = categories
                                        .filter((c) => !(draft.categories ?? []).includes(c.id))
                                        .filter(
                                            (c) =>
                                                !q ||
                                                (c.name ?? '').toLowerCase().includes(q)
                                        )
                                        .slice(0, 8);
                                    const allSelected =
                                        selectedCats.length === categories.length;
                                    return (
                                        <>
                                            <div
                                                className="d-flex flex-wrap gap-2"
                                                style={{ marginBottom: '0.5rem' }}
                                            >
                                                {selectedCats.length === 0 && (
                                                    <span className="meta">
                                                        {t('services.noneSelectedYet')}
                                                    </span>
                                                )}
                                                {selectedCats.map((c) => (
                                                    <span
                                                        key={c.id}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '0.4rem',
                                                            padding: '0.25rem 0.5rem 0.25rem 0.75rem',
                                                            background: '#e7e3ff',
                                                            color: '#3b2f8a',
                                                            borderRadius: 999,
                                                            fontSize: '0.85rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {c.name || '(no name)'}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleCategory(c.id)}
                                                            aria-label={`${t('common.remove')} ${c.name}`}
                                                            title={t('common.remove')}
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                color: 'inherit',
                                                                padding: 0,
                                                                margin: 0,
                                                                fontSize: '1rem',
                                                                lineHeight: 1,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>

                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder={
                                                        allSelected
                                                            ? t('services.allCategoriesSelected')
                                                            : t('services.addCategory')
                                                    }
                                                    value={catSearch}
                                                    disabled={allSelected}
                                                    onChange={(e) => setCatSearch(e.target.value)}
                                                    onFocus={() => setCatDropdownOpen(true)}
                                                    onBlur={() =>
                                                        setTimeout(
                                                            () => setCatDropdownOpen(false),
                                                            150
                                                        )
                                                    }
                                                    data-cy="service-category-select"
                                                />
                                                {catDropdownOpen &&
                                                    availableMatches.length > 0 && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                zIndex: 10,
                                                                background: 'var(--gs-surface, #fff)',
                                                                border: '1px solid var(--gs-border, #e6e6ec)',
                                                                borderRadius: 6,
                                                                boxShadow:
                                                                    '0 4px 14px rgba(0,0,0,0.08)',
                                                                maxHeight: 240,
                                                                overflowY: 'auto',
                                                                marginTop: 2,
                                                            }}
                                                        >
                                                            {availableMatches.map((c) => (
                                                                <button
                                                                    key={c.id}
                                                                    type="button"
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        toggleCategory(c.id);
                                                                        setCatSearch('');
                                                                    }}
                                                                    style={{
                                                                        display: 'block',
                                                                        width: '100%',
                                                                        textAlign: 'left',
                                                                        padding:
                                                                            '0.5rem 0.75rem',
                                                                        background:
                                                                            'transparent',
                                                                        border: 'none',
                                                                        borderBottom:
                                                                            '1px solid var(--gs-divider, #f0f0f3)',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    {c.name || '(no name)'}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                            </div>
                                        </>
                                    );
                                })()
                            )}
                        </div>

                        {/* Color + Duration on the same row — color uses the same
                            swatch input as the Business Details brand picker
                            (single rounded square that opens the native color
                            dialog), not the old pastel palette. */}
                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label>{t('services.serviceColor')}</label>
                                <div className="gs-color-swatch-row">
                                    <label
                                        className="gs-color-swatch"
                                        style={{ background: draft.color ?? '#0d6efd' }}
                                        title={t('services.pickColor')}
                                    >
                                        <input
                                            type="color"
                                            value={draft.color ?? '#0d6efd'}
                                            onChange={(e) => handleChange('color', e.target.value)}
                                        />
                                    </label>
                                    <code className="gs-color-swatch__hex">
                                        {(draft.color ?? '#0D6EFD').toUpperCase()}
                                    </code>
                                </div>
                            </div>
                            <div className="form-group" data-cy="service-duration-input">
                                <label>{t('services.serviceDuration')}</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input
                                        type="number"
                                        className="form-control"
                                        style={{ flex: 1 }}
                                        min={0}
                                        step={durationUnit === 'HOURS' ? 0.5 : 5}
                                        value={
                                            draft.totalDuration
                                                ? (durationUnit === 'HOURS'
                                                    ? Math.round((draft.totalDuration / 60) * 100) / 100 /* 2dp, no repeating decimal */
                                                    : draft.totalDuration)
                                                : '' /* empty instead of a stuck "0" */
                                        }
                                        onChange={(e) => {
                                            const n = e.target.value === '' ? 0 : Number(e.target.value);
                                            handleChange('totalDuration', durationUnit === 'HOURS' ? Math.round(n * 60) : n);
                                        }}
                                    />
                                    <select
                                        className="form-control"
                                        style={{ width: 'auto' }}
                                        data-cy="service-duration-unit"
                                        value={durationUnit}
                                        onChange={(e) => setDurationUnit(e.target.value as 'MINUTES' | 'HOURS')}
                                    >
                                        <option value="MINUTES">{t('services.unitMinutes')}</option>
                                        <option value="HOURS">{t('services.unitHours')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        </>
                        )}

                        {serviceTab === 'payment' && (
                        <>
                        {/* Price + Price type. */}
                        <div className="gs-form-grid">
                            <div className="form-group" data-cy="service-price-input">
                                <label>
                                    {(draft.kind ?? 'SERVICE') === 'EVENT'
                                        ? t('services.pricePerParticipant')
                                        : t('services.servicePrice')}
                                </label>
                                <MoneyInput
                                    value={draft.price != null ? String(draft.price) : ''}
                                    onChange={(v) => handleChange('price', v === '' ? 0 : Number(v))}
                                    currency={selectedBusiness?.currency}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('services.servicePriceType')}</label>
                                <select
                                    className="form-select"
                                    value={draft.priceType ?? 'FIXED'}
                                    onChange={(e) =>
                                        handlePriceTypeChange(e.target.value as PriceType)
                                    }
                                    data-cy="service-price-type-select"
                                >
                                    {PRICE_TYPE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {t(o.value === 'VARIABLE' ? 'services.priceVariable' : 'services.priceFixed')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Per-service tax override. Empty = inherit the company default. */}
                        <div className="gs-form-grid">
                            <div className="form-group" data-cy="service-tax-input">
                                <label>{t('services.taxRate')}</label>
                                <NumberWithSuffix
                                    id="service-tax-rate"
                                    value={draft.taxRate ?? ''}
                                    onChange={(v) => handleChange('taxRate', v.trim() === '' ? null : Number(v))}
                                    suffix="%"
                                    placeholder={companyTaxRate != null
                                        ? t('services.taxRateInherit', { rate: companyTaxRate })
                                        : t('services.taxRatePlaceholder')}
                                />
                                <small className="form-text text-muted">{t('services.taxRateHint')}</small>
                            </div>
                        </div>

                        {/* Deposit. Disabled when the service accepts a health plan
                            — a covered appointment isn't booked with a deposit.
                            NOT shown for EVENT types: an event's deposit is decided
                            per occurrence when it's scheduled on the calendar (it can
                            change between editions), so it lives on the EventDrawer. */}
                        {(draft.kind ?? 'SERVICE') !== 'EVENT' && (
                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label className={`gs-inline-check${acceptsConvenio ? ' gs-inline-check--disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={!!draft.depositRequired}
                                        disabled={acceptsConvenio}
                                        onChange={(e) =>
                                            handleChange('depositRequired', e.target.checked)
                                        }
                                        data-cy="service-deposit-required-checkbox"
                                    />
                                    <span className="gs-inline-check__label">{t('services.depositRequired')}</span>
                                </label>
                                {acceptsConvenio && (
                                    <div className="form-text">{t('services.depositConvenioDisabled')}</div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>{t('services.depositAmount')}</label>
                                <MoneyInput
                                    value={
                                        draft.amountDepositRequired != null
                                            ? String(draft.amountDepositRequired)
                                            : ''
                                    }
                                    onChange={(v) =>
                                        handleChange(
                                            'amountDepositRequired',
                                            v === '' ? 0 : Number(v)
                                        )
                                    }
                                    currency={selectedBusiness?.currency}
                                    disabled={acceptsConvenio || !draft.depositRequired}
                                />
                            </div>
                        </div>
                        )}

                        {/* Health plans (convênios) accepted by this service. The
                            catalog is curated per-business in Settings → Payment;
                            here you pick which apply and the negotiated value of
                            each (the plan's "tabela" for this service). */}
                        {(() => {
                            const isVariable = draft.priceType === 'VARIABLE';
                            return (
                                <div className="form-group">
                                    <label className={`gs-inline-check${isVariable ? ' gs-inline-check--disabled' : ''}`}>
                                        <input
                                            type="checkbox"
                                            className="gs-inline-check__box"
                                            checked={acceptsConvenio}
                                            disabled={isVariable}
                                            onChange={(e) => toggleAcceptsConvenio(e.target.checked)}
                                            data-cy="service-accepts-convenio-checkbox"
                                        />
                                        <span className="gs-inline-check__label">{t('services.acceptsConvenio')}</span>
                                    </label>
                                    <div className="form-text">
                                        {isVariable
                                            ? t('services.convenioVariableDisabled')
                                            : t('services.acceptsConvenioHint')}
                                    </div>
                                </div>
                            );
                        })()}

                        {acceptsConvenio && (
                            healthPlans.length === 0 ? (
                                <div className="empty-hint" style={{ padding: '0.25rem 0 0.75rem' }}>
                                    {t('services.convenioNoPlans')}
                                </div>
                            ) : (
                                <div className="gs-convenio-list" data-cy="service-convenio-list">
                                    {healthPlans.map((plan) => {
                                        const planId = Number(plan.id);
                                        const selected = isPlanAccepted(planId);
                                        const row = draft.acceptedPlans?.find((p) => p.healthPlanId === planId);
                                        const sym = currencySymbol(selectedBusiness?.currency);
                                        const table = row?.negotiatedValue ?? null;
                                        const repType: RepasseType = row?.repasseType ?? 'VALUE';
                                        const repVal = row?.repasseValue ?? null;
                                        // Validation flags (also blocked on Save + rejected by backend).
                                        const tableOver = selected && table != null
                                            && typeof draft.price === 'number' && table > draft.price;
                                        const repPercentBad = selected && repType === 'PERCENT'
                                            && repVal != null && repVal > 100;
                                        const repValueBad = selected && repType === 'VALUE' && repVal != null
                                            && table != null && repVal > table;
                                        const effRepasse = row ? effectiveRepasse(row) : 0;
                                        return (
                                            <div
                                                key={plan.id}
                                                className="gs-convenio-row"
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.4rem',
                                                    padding: '0.55rem 0',
                                                    borderBottom: '1px solid var(--gs-divider, #f0f0f3)',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <label className="gs-inline-check" style={{ flex: 1, margin: 0 }}>
                                                        <input
                                                            type="checkbox"
                                                            className="gs-inline-check__box"
                                                            checked={selected}
                                                            onChange={() => togglePlan(planId)}
                                                            data-cy={`service-convenio-toggle-${plan.id}`}
                                                        />
                                                        <span className="gs-inline-check__label">{plan.name}</span>
                                                    </label>
                                                    {selected && (
                                                        <span style={{ fontSize: '0.78rem', color: 'var(--gs-text-soft)', whiteSpace: 'nowrap' }}>
                                                            {t('services.convenioReceivable')}: <strong>{sym}{effRepasse.toFixed(2)}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                                {selected && (
                                                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingLeft: '1.6rem' }}>
                                                        {/* Valor de tabela */}
                                                        <div style={{ width: 150, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <label className="small text-muted mb-0" style={{ fontSize: '0.72rem' }}>
                                                                {t('services.convenioTableValue')}
                                                            </label>
                                                            <MoneyInput
                                                                className={tableOver ? 'gs-money-invalid' : undefined}
                                                                value={table != null ? String(table) : ''}
                                                                onChange={(v) => updatePlan(planId, { negotiatedValue: v === '' ? null : Number(v) })}
                                                                currency={selectedBusiness?.currency}
                                                                placeholder={t('services.convenioValuePlaceholder')}
                                                            />
                                                            {typeof draft.price === 'number' && draft.price > 0 && (
                                                                <small className={tableOver ? 'text-danger' : 'text-muted'}
                                                                    style={{ fontSize: '0.7rem', fontWeight: tableOver ? 600 : 400 }}>
                                                                    {t('services.convenioValueMax', { amount: `${sym}${draft.price.toFixed(2)}` })}
                                                                </small>
                                                            )}
                                                        </div>
                                                        {/* Repasse: % ou valor */}
                                                        <div style={{ width: 190, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <label className="small text-muted mb-0" style={{ fontSize: '0.72rem' }}>
                                                                {t('services.convenioRepasse')}
                                                            </label>
                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                <div className="gs-pill-group" role="group" aria-label="Repasse">
                                                                    <button
                                                                        type="button"
                                                                        className={`gs-pill-toggle ${repType === 'VALUE' ? 'is-active' : ''}`}
                                                                        onClick={() => updatePlan(planId, { repasseType: 'VALUE' })}
                                                                        data-cy={`service-convenio-repasse-value-${plan.id}`}
                                                                    >
                                                                        {sym}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`gs-pill-toggle ${repType === 'PERCENT' ? 'is-active' : ''}`}
                                                                        onClick={() => updatePlan(planId, { repasseType: 'PERCENT' })}
                                                                        data-cy={`service-convenio-repasse-percent-${plan.id}`}
                                                                    >
                                                                        %
                                                                    </button>
                                                                </div>
                                                                {repType === 'PERCENT' ? (
                                                                    <NumberWithSuffix
                                                                        value={repVal ?? ''}
                                                                        onChange={(v) => updatePlan(planId, { repasseValue: v.trim() === '' ? null : Number(v) })}
                                                                        suffix="%"
                                                                        min={0}
                                                                        placeholder="0"
                                                                    />
                                                                ) : (
                                                                    <MoneyInput
                                                                        className={repValueBad ? 'gs-money-invalid' : undefined}
                                                                        value={repVal != null ? String(repVal) : ''}
                                                                        onChange={(v) => updatePlan(planId, { repasseValue: v === '' ? null : Number(v) })}
                                                                        currency={selectedBusiness?.currency}
                                                                        placeholder={t('services.convenioValuePlaceholder')}
                                                                    />
                                                                )}
                                                            </div>
                                                            {(repPercentBad || repValueBad) && (
                                                                <small className="text-danger" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                                                    {repPercentBad
                                                                        ? t('services.convenioRepassePercentMax')
                                                                        : t('services.convenioRepasseValueMax', { amount: `${sym}${(table ?? 0).toFixed(2)}` })}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                        </>
                        )}

                        {serviceTab === 'booking' && (
                        <>
                        {/* Online availability + house-call toggles, side by side. */}
                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label className="gs-inline-check">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={draft.allowOnlineBooking !== false}
                                        onChange={(e) =>
                                            handleChange('allowOnlineBooking', e.target.checked)
                                        }
                                        data-cy="service-allow-online-booking-checkbox"
                                    />
                                    <span className="gs-inline-check__label">{t('services.allowOnlineBooking')}</span>
                                </label>
                                <div className="form-text">
                                    {t('services.allowOnlineBookingHint')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="gs-inline-check">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={!!draft.allowExternalLocation}
                                        onChange={(e) =>
                                            handleChange('allowExternalLocation', e.target.checked)
                                        }
                                        data-cy="service-allow-external-location-checkbox"
                                    />
                                    <span className="gs-inline-check__label">{t('services.allowExternalLocation')}</span>
                                </label>
                                <div className="form-text">
                                    {t('services.allowExternalLocationHint')}
                                </div>
                            </div>
                        </div>

                        {/* Booking window + preparation buffers (per service). */}
                        <div className="gs-form-grid gs-form-grid--3">
                            <div className="form-group">
                                <label>{t('services.bookingWindow')}</label>
                                <NumberWithSuffix
                                    value={draft.bookingWindow ?? ''}
                                    onChange={(v) => handleChange('bookingWindow', v === '' ? null : Number(v))}
                                    suffix={t('services.bookingWindowSuffix')}
                                    min={0}
                                    step={5}
                                />
                                <div className="form-text">{t('services.bookingWindowHint')}</div>
                            </div>
                            <div className="form-group">
                                <label>{t('services.preparationBefore')}</label>
                                <NumberWithSuffix
                                    value={draft.startPreparationDuration ?? ''}
                                    onChange={(v) => handleChange('startPreparationDuration', v === '' ? null : Number(v))}
                                    suffix={t('services.preparationSuffix')}
                                    min={0}
                                    step={5}
                                />
                                <label className="gs-inline-check" style={{ marginTop: '0.4rem' }}>
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={!!draft.allowBookingDuringPreparationDuration}
                                        onChange={(e) => handleChange('allowBookingDuringPreparationDuration', e.target.checked)}
                                    />
                                    <span className="gs-inline-check__label">{t('services.allowBookingDuringPrep')}</span>
                                </label>
                            </div>
                            <div className="form-group">
                                <label>{t('services.cleanupAfter')}</label>
                                <NumberWithSuffix
                                    value={draft.endAfterDuration ?? ''}
                                    onChange={(v) => handleChange('endAfterDuration', v === '' ? null : Number(v))}
                                    suffix={t('services.cleanupSuffix')}
                                    min={0}
                                    step={5}
                                />
                                <label className="gs-inline-check" style={{ marginTop: '0.4rem' }}>
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={!!draft.allowBookingDuringAfterDuration}
                                        onChange={(e) => handleChange('allowBookingDuringAfterDuration', e.target.checked)}
                                    />
                                    <span className="gs-inline-check__label">{t('services.allowBookingDuringCleanup')}</span>
                                </label>
                            </div>
                        </div>

                        {healthMode && (
                            <div className="form-group" data-cy="service-health-override">
                                <label>{t('services.healthOverrideLabel')}</label>
                                <select
                                    className="form-select"
                                    value={draft.isHealthDataOverride == null ? 'inherit' : draft.isHealthDataOverride ? 'yes' : 'no'}
                                    onChange={(e) => handleChange('isHealthDataOverride',
                                        e.target.value === 'inherit' ? null : e.target.value === 'yes')}
                                >
                                    <option value="inherit">
                                        {t('services.healthOverrideInherit', {
                                            value: (() => {
                                                const cats = categories.filter((c) => (draft.categories ?? []).includes(c.id));
                                                if (cats.length === 0) return t('services.healthUnclassifiedBadge');
                                                if (cats.some((c) => c.isHealthData === true)) return t('common.yes');
                                                if (cats.every((c) => c.isHealthData === false)) return t('common.no');
                                                return t('services.healthUnclassifiedBadge');
                                            })(),
                                        })}
                                    </option>
                                    <option value="yes">{t('services.healthOverrideYes')}</option>
                                    <option value="no">{t('services.healthOverrideNo')}</option>
                                </select>
                                <small className="text-muted">{t('services.healthOverrideHint')}</small>
                            </div>
                        )}

                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label className="gs-inline-check">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={!!draft.allowRecurring}
                                        onChange={(e) =>
                                            handleChange('allowRecurring', e.target.checked)
                                        }
                                        data-cy="service-allow-recurring-checkbox"
                                    />
                                    <span className="gs-inline-check__label">
                                        {t('services.allowRecurring')}
                                    </span>
                                </label>
                            </div>
                        </div>

                        {draft.allowRecurring && (
                            <div className="gs-form-grid">
                                <div className="form-group">
                                    <label>{t('services.repeatOptions')}</label>
                                    <div className="gs-chip-row">
                                        {RECURRENCE_FREQ_KEYS.map((freq) => {
                                            const on = (draft.recurrenceFreqs ?? []).includes(freq);
                                            return (
                                                <button
                                                    type="button"
                                                    key={freq}
                                                    className={`gs-chip${on ? ' gs-chip--on' : ''}`}
                                                    onClick={() => toggleRecurrenceFreq(freq)}
                                                >
                                                    {t(`services.freq.${freq}`)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <small className="text-muted">
                                        {t('services.repeatOptionsHint')}
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>{t('services.maxHorizon')}</label>
                                    <select
                                        className="form-select"
                                        value={draft.maxRecurrenceMonths ?? 3}
                                        onChange={(e) =>
                                            handleChange(
                                                'maxRecurrenceMonths',
                                                Number(e.target.value)
                                            )
                                        }
                                        data-cy="service-max-recurrence-select"
                                    >
                                        <option value={1}>{t('services.maxHorizon1')}</option>
                                        <option value={3}>{t('services.maxHorizon3')}</option>
                                        <option value={6}>{t('services.maxHorizon6')}</option>
                                        <option value={12}>{t('services.maxHorizon12')}</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        </>
                        )}

                    </form>
                )}
            </Drawer>
        </>
    );
};

export default ServicesTab;
